import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ContentComponent } from '../../modules/shell/content/content.component';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { ScreenST } from './shell-states/screen.st';
import { MenuST } from './shell-states/menu.st';
import { UserAccountST } from './shell-states/user-account.st';
import { BreadCrumbST } from './shell-states/breadcrumb.st';
import { RouterConstant } from 'src/app/constants/router.constant';
import { BienvenidaResponseDTO } from 'src/app/dtos/seguridad/bienvenida/bienvenida-response.dto';
import { AutenticacionResponseDTO } from 'src/app/dtos/seguridad/autenticacion/autenticacion-response.dto';
import { VentanaModalModel } from 'src/app/model-component/ventana-modal.model';
import { SessionStoreUtil } from 'src/app/utilities/session-store.util';
import { TipoEventoConstant } from 'src/app/constants/tipo-evento.constant';
import * as moment from 'moment';
import { UsuarioIdentificacionDTO } from 'src/app/dtos/seguridad/autenticacion/usuario-identificacion.dto';
import { SeguridadService } from 'src/app/modules/login/seguridad.service';
import { AutenticacionAPIConstant } from 'src/app/constants/apis/seguridad/autenticacion-api.constant';
import { LoginSingOutRequestDTO } from 'src/app/dtos/seguridad/autenticacion/login-singioutrequest.dto';
import { DeviceInfoDTO } from 'src/app/dtos/seguridad/autenticacion/device-info.dto';
import { DateFormatConstant } from 'src/app/constants/dateFormat.constant';
import { AuthTokenService } from 'src/app/modules/login/auth.service';
import { SessionReasonConstant } from 'src/app/constants/session.constant';

type RefreshTokenRequest = {
  userIdentification: UserIdentification;
  refreshToken: string;
}

type UserIdentification = {
  documentType: string;
  documentNumber: string;
}

/**
 * Se utiliza para administrar el estado del Shell de la aplicacion
 */
@Injectable({ providedIn: 'root' })
export class ShellState {

  /** Administra el estado de la pantalla del dispositivo */
  public screen: ScreenST;

  /** Administra el estado de la miga de pan */
  public breadCrumb: BreadCrumbST;

  /** Administra el estado del menu de la aplicacion */
  public menu: MenuST;

  /** Administra el estado de la cuenta de usuario */
  public userAccount: UserAccountST;

  /** Componente del shell content para manipular el scroll del DIV content main */
  public contentComponent: ContentComponent | any;

  /** Se utiliza para visualizar el modal timeout */
  public modalTimeOut: VentanaModalModel | any;

  /** cuenta regresiva para el timeout */
  public countdownIdle: string | any;

  /** Datos del usuario */
  public auth: AutenticacionResponseDTO;

  public reason = "EXPIRATION";

  private static readonly LOGOUT_EVENT_KEY = 'app-logout-event';
  private static readonly KEY_AUTENTICACION = 'AUTH_PERSIST';
  private static readonly MENU_KEY = 'MENU_PERSIST';

  private refreshTokenIntervalId: ReturnType<typeof setInterval> | null = null;

  private storageEventHandler = (event: StorageEvent): void => {
    if (event.key === ShellState.LOGOUT_EVENT_KEY && event.newValue) {
      this.handleRemoteLogout(event.newValue);
    }
  };

  private accessToken: string = "";
  private idtoken: string = "";

  /**
   * @param router, se utiliza para ser notificado cuando el router cambia
   * @param idle, se utiliza para activar el timeout
   * @param keepalive, se utiliza para hacer ping en la conexion
   */
  constructor(
    private router: Router,
    private idle: Idle,
    private keepalive: Keepalive,
    private seguridadService: SeguridadService,
    private authTokenService: AuthTokenService
  ) {
    this.auth = SessionStoreUtil.auth(TipoEventoConstant.GET);
    if (this.auth?.usuario?.idUsuario) {
      if (this.wasPageReloaded() || this.authTokenService.hasLivePresence(this.auth.usuario.idUsuario)) {
        this.authTokenService.claimTabOwnership(this.auth.usuario.idUsuario);
      } else {
        SessionStoreUtil.cleanAll();
        this.auth = null as any;
      }
    }
    this.authTokenService.onOwnershipLost.subscribe(() => this.handleTabEvicted());
    setInterval(() => {
      this.validaProgramacion();

    }, 300000);

    // Estado para notificar el tamanio de la pantalla
    this.screen = new ScreenST();

    // Estado para administrar la cuenta del usuario
    this.userAccount = new UserAccountST();

    // Estado para administar la miga de pan
    this.breadCrumb = new BreadCrumbST();

    // Se utiliza para administrar el estado del Menu
    this.menu = new MenuST(this.screen, this.breadCrumb, this.router);

    // Se configura el timeout
    this.initIdle();

    // Se inicia el time out si existe una autenticacion
    this.initTimeOut();

    this.validarRefreshTokenRefrescarPagina();

    window.addEventListener('storage', this.storageEventHandler);
  }

  saveAuth(dataAutenticacion: AutenticacionResponseDTO) {
    this.userAccount.changeStateAutenticado(dataAutenticacion);
    if (dataAutenticacion?.usuario?.idUsuario) {
      this.authTokenService.claimTabOwnership(dataAutenticacion.usuario.idUsuario);
    }
    localStorage.removeItem(ShellState.LOGOUT_EVENT_KEY);
  }

  /**
   *
   * Metodo que soporta el evento iniciar sesion del login
   *
   * @param dataBienvenida, DTO con los datos de bienvenida
   * @param dataAutenticacion, DTO con los datos de la autenticacion
   */
  public iniciarSesion(
    dataBienvenida: BienvenidaResponseDTO): void {

    // se cambia el estado de la cuenta a sesion iniciada
    // se construye el menu de la aplicacion
    this.menu.initMenu(dataBienvenida.itemsMenu);

    // se inicia el timeout sesion
    this.idle.watch();

    // se redirecciona a la pagina de bienvenida
    this.router.navigate([RouterConstant.NAVIGATE_BIENVENIDA]);
      
    this.expiredRefreshTokenInit();

  }

  /**
   * Metodo que soporta el evento cerrar sesion del menu
   */
  public cerrarSesion(reason: string): void {

    this.auth = SessionStoreUtil.auth(TipoEventoConstant.GET);

    const userIdentification: UsuarioIdentificacionDTO = {
      documentType: this.auth.documentType,
      documentNumber: this.auth.documentNumber
    }
    const deviceInfo: DeviceInfoDTO = {
      identifier: this.auth.deviceInfo.identifier,
      remoteAddress: this.auth.deviceInfo.remoteAddress,
      technology: this.auth.deviceInfo.technology,
      technologyVersion: this.auth.deviceInfo.technologyVersion,
      platform: this.auth.deviceInfo.platform
    }

    const singioutRequest: LoginSingOutRequestDTO = {
      userIdentification,
      applicationType: AutenticacionAPIConstant.APPLICATION_TYPE,
      deviceInfo,
      reason: reason
    }
    this.seguridadService.signout(singioutRequest)
      .subscribe(() => {

          // se cambia el estado de la cuenta a sesion cerrada
          this.userAccount.changeStateSesionCerrada();

          // se para el timeout sesion
          this.idle.stop();

          if (this.refreshTokenIntervalId) {
            clearInterval(this.refreshTokenIntervalId);
            this.refreshTokenIntervalId = null;
          }

          localStorage.setItem(ShellState.LOGOUT_EVENT_KEY, JSON.stringify({
            timestamp: Date.now(),
            userId: this.auth.usuario.idUsuario
          }));

          // se redirecciona al LOGIN
          window.location.reload();

          setTimeout(() => {
            // se destruye el menu para limpiar memoria
            this.menu.destroyMenu();
          }, 400);

          this.authTokenService.releaseTabOwnership(this.auth.usuario.idUsuario);
          const userScopedKeys = [
            ShellState.KEY_AUTENTICACION,
            ShellState.MENU_KEY,
            'rolesAsignados',
            'puntoVenta',
            'cajaVenta'
          ];
          userScopedKeys.forEach(key => localStorage.removeItem(key));
      });

  }

  /**
   * Metodo que soporta el evento click del boton permanecer del modal timeout
   */
  public permanecer(): void {
    this.idle.watch();
    this.modalTimeOut.closeModal();
  }

  /**
   * Metodo que soporta el evento click del boton salir del modal timeout
   */
  public salir(): void {

    this.cerrarSesion(this.reason);
    this.modalTimeOut.closeModal();
  }

  /**
   * Metodo que permite configurar el timeout
   */
  private initIdle(): void {

    // se configura el tiempo del timeout por 15 minutos
    this.idle.setIdle(900);

    // se configura el timeout periodo
    this.idle.setTimeout(15);

    // establece las interrupciones predeterminadas, en este caso,
    // cosas como clics, desplazamientos, toques del documento
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // ping de conexion cada 15 segundos
    this.keepalive.interval(15);

    // se activa cuando dectecta algun action
    this.idle.onIdleEnd.subscribe(() => {
      this.idle.watch();
    });

    // se cierra sesion cuando se cumple timeout
    this.idle.onTimeout.subscribe(() => {
      this.salir();
    });

    // se activa cuando se empieza la cuenta regresiva
    this.idle.onIdleStart.subscribe(() => {
      // se abre el modal
      if (this.modalTimeOut == null) {
        this.modalTimeOut = new VentanaModalModel();
      }
      this.modalTimeOut.showModal(null);
    });

    // cuenta regresiva para el timeout
    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.countdownIdle = countdown.toString();
    });

  }

  /**
   * Metodo que permite configurar el timeOut cuando
   * existe una autenticacion en el session store
   */
  private initTimeOut() {

    if (this.auth && this.auth.usuario && this.auth.usuario.idUsuario) {
      this.idle.watch();
    }
  }


  /**
   * Método que permite verificar si la programación del vendedor aún está activa
   */
  private validaProgramacion(): void {
    // se cierra sesion cuando se cumple timeout
    if (this.auth && this.auth.usuario && this.auth.usuario.horaFinal) {
      const horaActual = moment(moment().format(DateFormatConstant.HOUR_FORMAT_V0), DateFormatConstant.HOUR_FORMAT_V0);
      const horaProgramada = moment(this.auth.usuario.horaFinal, DateFormatConstant.HOUR_FORMAT_V0);

      if (horaProgramada) {

        const result = (moment(horaActual).isAfter(moment(horaProgramada)));
        if (result) {

          this.cerrarSesion(this.reason);

        }
      }
    }
  }

  private validarRefreshTokenRefrescarPagina() {
    if (this.auth?.accessToken) {
      this.expiredRefreshTokenInit();
    }
  }

  private runRefreshTokenService(refreshTokenRequest: RefreshTokenRequest, refreshTokenLoop: boolean = true) {
    this.seguridadService.refreshToken(refreshTokenRequest)
      .subscribe({
        next: (response: any) => {
          this.auth.idToken = response.idToken;
          this.auth.accessToken = response.accessToken;
          this.userAccount.changeStateAutenticado(this.auth);
          const expiredTimeTokenMilliseconds: number = 1000 * parseInt(response.expiresInSeconds) - 60000;   
          if (!refreshTokenLoop) return;
          this.expiredRefreshTokenLoop(refreshTokenRequest, expiredTimeTokenMilliseconds);
        },
        error: () => {
          if (this.refreshTokenIntervalId) {
            clearInterval(this.refreshTokenIntervalId);
            this.refreshTokenIntervalId = null;
          }
          this.cerrarSesion(this.reason);
        }
      })
  }
  
  private expiredRefreshTokenInit() {
    this.auth = SessionStoreUtil.auth(TipoEventoConstant.GET); 
    const refreshTokenRequest: RefreshTokenRequest = {
      "userIdentification": {
        "documentType": this.auth.documentType,
        "documentNumber": this.auth.documentNumber
      },
      "refreshToken": this.auth.token
    }

    this.runRefreshTokenService(refreshTokenRequest);
  }

  private expiredRefreshTokenLoop(requestRefresh: RefreshTokenRequest, expiredTimeToken: number) {    
    this.auth = SessionStoreUtil.auth(TipoEventoConstant.GET);
    this.refreshTokenIntervalId = setInterval(() => {      
      this.runRefreshTokenService(requestRefresh, false);
    }, expiredTimeToken);
  }

  private handleRemoteLogout(rawEvent: string): void {
    if (!this.auth?.usuario?.idUsuario) {
      return;
    }

    let loggedOutUserId: number | string | undefined;
    try {
      loggedOutUserId = JSON.parse(rawEvent)?.userId;
    } catch {
      loggedOutUserId = this.auth.usuario.idUsuario;
    }

    if (loggedOutUserId !== this.auth.usuario.idUsuario) {
      return;
    }

    if (this.refreshTokenIntervalId) {
      clearInterval(this.refreshTokenIntervalId);
      this.refreshTokenIntervalId = null;
    }
    this.userAccount.changeStateSesionCerrada();
    this.idle.stop();
    this.menu.destroyMenu();
    window.location.reload();
  }

  private handleTabEvicted(): void {
    const evictedUserId = this.auth?.usuario?.idUsuario;

    if (this.refreshTokenIntervalId) {
      clearInterval(this.refreshTokenIntervalId);
      this.refreshTokenIntervalId = null;
    }
    this.idle.stop();
    this.menu.destroyMenu();

    SessionStoreUtil.cleanAll();
    sessionStorage.setItem('tab-evicted', 'true');
    this.auth = null as any;
    this.authTokenService.clearTemporaryToken();

    if (evictedUserId) {
      this.authTokenService.releaseTabOwnership(evictedUserId);
    }

    this.router.navigate([RouterConstant.NAVIGATE_LOGIN], {
      queryParams: { reason: SessionReasonConstant.EVICTED }
    });
  }

  private wasPageReloaded(): boolean {
    try {
      const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      return entries.length > 0 && entries[0].type === 'reload';
    } catch {
      return false;
    }
  }

  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageEventHandler);
  }
}
